/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

// User Roles enum
export enum UserRole {
  ADMIN = 'ADMIN',
  CONTENT_ADMIN = 'CONTENT_ADMIN',
  POWER_USER = 'POWER_USER',
  USER = 'USER',
  AUDITOR = 'AUDITOR',
  INSPECTOR = 'INSPECTOR',
  EXTERNAL = 'EXTERNAL',
  CLOUD_ADMIN_ROLE = 'CLOUD_ADMIN_ROLE',
  RCR_USER = 'RCR_USER',
  ETMF_MANAGER = 'ETMF_MANAGER',
  ETMF_SPECIALIST = 'ETMF_SPECIALIST',
  ETMF_SITE_USER = 'ETMF_SITE_USER',
  ETMF_STUDY_USER = 'ETMF_STUDY_USER',
  EOS_SITE_USER = 'EOS_SITE_USER',
  ETMF_SPONSOR = 'ETMF_SPONSOR',
  STUDY_ADMIN = 'STUDY_ADMIN'
}

// User Permissions enum
export enum UserPermission {
  MANAGE_ETMF = 'manage_etmf',
  MANAGE_STUDY_ETMF = 'manage_study_etmf',
  UPLOAD_SITE_ARTIFACT = 'upload_site_artifact',
  ACCESS_STUDY_STAGING_AREA = 'access_study_staging_area',
  ACCESS_RAVE_CONTENT_REPOSITORY = 'access_rave_content_repository',
  FULL_RCR_DOCUMENT = 'full_rcr_document',
  READ_RCR_DOCUMENT = 'read_rcr_document',
  VIEW_STUDY_ETMF = 'view_study_etmf'
}

// User Group definitions
export enum UserGroup {
  RCM_ADMINS = 'rcmAdmins',
  RCM_USERS = 'rcmUsers',
  CONTENT_ADMINS = 'contentAdmins',
  REPORTS_ACCESS_ADMINS = 'reportsAccessAdmins',
  STAGING_ACCESS = 'stagingAccess',
  STAGING_USERS = 'stagingUsers',
  STAGING_MANAGERS = 'stagingManagers',
  POWER_USER_PLUS = 'powerUserPlus',
  EOS_STUDY_ADMINS = 'eosStudyAdmins'
}

// Access Rule identifiers
export enum AccessRule {
  // System sections
  SYSTEM_ACCESS = 'systemAccess',
  SYSTEM_ACCESS_USERS = 'systemAccessUsers',
  SYSTEM_ACCESS_GROUPS = 'systemAccessGroups',
  SYSTEM_ACCESS_POLICIES = 'systemAccessPolicies',
  SYSTEM_ACCESS_TEXT_AND_MESSAGES = 'systemAccessTextAndMessages',
  SYSTEM_ACCESS_SYSTEM_PERFORMANCE = 'systemAccessSystemPerformance',
  SYSTEM_ACCESS_ETMF_ACCESS = 'systemAccessEtmfAccess',
  SYSTEM_ACCESS_INTEGRATIONS = 'systemAccessIntegrations',
  SYSTEM_ACCESS_OPERATING_MODE = 'systemAccessOperatingMode',
  SYSTEM_ACCESS_UPGRADE_VALIDATION = 'systemAccessUpgradeValidation',
  FSI_SETTINGS = 'fsiSettings',
  FSI_PROPERTIES = 'fsiProperties',

  // Settings
  SETTINGS_ACCESS = 'settingsAccess',
  SETTINGS_ACCESS_EDIT_PROFILE = 'settingsAccessEditProfile',
  CLINICAL_ACCESS = 'clinicalAccess',

  // Reports sections
  REPORTS_ACCESS = 'reportsAccess',
  REPORTS_ACCESS_ACTIVITY = 'reportsAccessActivity',
  REPORTS_ACCESS_CLINICAL_ACTIVITY = 'reportsAccessClinicalActivity',
  REPORTS_ACCESS_ACCESS_RIGHTS = 'reportsAccessAccessRights',
  REPORTS_ACCESS_READ_ACKNOWLEDGE = 'reportsAccessReadAcknowledge',
  REPORTS_ACCESS_USERS_USAGE = 'reportsAccessUsersUsage',
  REPORTS_ACCESS_GROUP_MEMBERSHIP = 'reportsAccessGroupMembership',
  REPORTS_ACCESS_CHANGE_REQUESTS = 'reportsAccessChangeRequests',
  REPORTS_ACCESS_CHANGE_REQUEST_TYPES = 'reportsAccessChangeRequestTypes',
  REPORTS_ACCESS_PROPERTIES_SEARCH = 'reportsAccessPropertiesSearch',
  REPORTS_ACCESS_PROPERTIES_USAGE = 'reportsAccessPropertiesUsage',
  REPORTS_ACCESS_TASKS = 'reportsAccessTasks',
  REPORTS_ACCESS_REJECTION_REASONS = 'reportsAccessRejectionReasons',
  REPORTS_ACCESS_DOCUMENT_EXPIRATION = 'reportsAccessDocumentExpiration',
  REPORTS_ACCESS_CUSTOM = 'reportsAccessCustom',
  REPORTS_ACCESS_EOS_DOWNLOAD_ACTIVITY = 'reportsAccessEosDownloadActivity',
  REPORTS_ACCESS_EOS_ACKNOWLEDGEMENT_ACTIVITY = 'reportsAccessEosAcknowledgementActivity',
  REPORTS_EOS = 'reportsEos',
  REPORTS_ACCESS_ETMF_COMPLETION = 'reportsAccessEtmfCompletion',
  REPORTS_ACCESS_ETMF_DOC_DETAIL = 'reportsAccessEtmfDocDetail',
  REPORTS_ACCESS_ETMF_FILED_DOCS = 'reportsAccessEtmfFiledDocs',
  REPORTS_ACCESS_ETMF_CONTRIBUTOR_DOC_STATUS = 'reportsAccessEtmfContributorDocStatus',
  REPORTS_ACCESS_DUPLICATE_DOCUMENTS = 'reportsAccessDuplicateDocuments',
  REPORTS_ACCESS_QC_REVIEW = 'reportsAccessQCReview',
  REPORTS_ACCESS_ETMF_TIMELINESS = 'reportsAccessEtmfTimeliness',

  // EOS Media Access
  EOS_MEDIA_ACCESS = 'eosMediaAccess',
  EOS_MEDIA_ACCESS_OVERVIEW = 'eosMediaAccessOverview',
  EOS_MEDIA_ACCESS_TRANSFER_MEDIA = 'eosMediaAccessTransferMedia',
  EOS_MEDIA_ACCESS_SET_UP = 'eosMediaAccessSetUp',
  EOS_MEDIA_ACCESS_DOWNLOAD_MEDIA = 'eosMediaAccessDownloadMedia',
  EOS_MEDIA_ACCESS_SET_UP_POWER_USER = 'eosMediaAccessSetUpPowerUser',

  // Filter and Notifications
  FILTER_ROW_HIGH_RESTRICTION = 'filterRowHighRestriction',
  NOTIFICATIONS_ACTIONS = 'notificationsActions',

  // Tasks
  TASKS_ACCESS = 'tasksAccess',
  TASKS_ACCESS_ALL_OPEN = 'tasksAccessAllOpen',
  TASKS_FILTERS = 'tasksFilters',

  // Workflows
  WORKFLOWS_FILTERS = 'workflowsFilters',
  WORKFLOWS_ACCESS = 'workflowsAccess',
  WORKFLOWS_ACCESS_DETAIL = 'workflowsAccessDetail',

  // Documents
  DOCUMENTS_ACCESS = 'documentsAccess',
  DOCUMENTS_SELECT_MULTI_FILES = 'documentsSelectMultiFiles',
  DOCUMENTS_ACCESS_PROPERTIES_SEARCH = 'documentsAccessPropertiesSearch',
  DOCUMENTS_ACCESS_RECENT = 'documentsAccessRecent',
  DOCUMENTS_ACCESS_PERIODIC_REVIEW = 'documentsAccessPeriodicReview',
  DOCUMENTS_ACCESS_REGULATED_CONTENT = 'documentsAccessRegulatedContent',
  DOCUMENTS_ACCESS_FORMS = 'documentsAccessForms',
  DOCUMENTS_ACCESS_TEMPLATES = 'documentsAccessTemplates',
  DOCUMENTS_ACCESS_QC_REVIEW = 'documentsAccessQcReview',
  DOCUMENTS_ACCESS_CHANGE_REQUESTS = 'documentsAccessChangeRequests',
  DOCUMENTS_FILTER_OBSOLETE = 'documentsFilterObsolete',
  SEARCH_FILTER_OBSOLETE = 'searchFilterObsolete',
  SHOW_FILE_ACCESS = 'showFileAccess',

  // Homepage
  HOMEPAGE_ACCESS_REPORTS = 'homepageAccessReports',
  HOMEPAGE_ACCESS_RECENT = 'homepageAccessRecent',
  HOMEPAGE_ACCESS_PERIODIC_REVIEW = 'homepageAccessPeriodicReview',
  HOMEPAGE_ACCESS_TASKS = 'homepageAccessTasks',
  HOMEPAGE_ACCESS_FAVORITES = 'homepageAccessFavorites',
  HOMEPAGE_ACCESS_WORKFLOWS = 'homepageAccessWorkflows',

  // Group access rules (allows direct group reference like legacy AngularJS)
  // These enable hasAccess('stagingUsers') style calls that reference groups directly
  RCM_ADMINS = 'rcmAdmins',
  RCM_USERS = 'rcmUsers',
  CONTENT_ADMINS = 'contentAdmins',
  REPORTS_ACCESS_ADMINS = 'reportsAccessAdmins',
  STAGING_ACCESS = 'stagingAccess',
  STAGING_USERS = 'stagingUsers',
  STAGING_MANAGERS = 'stagingManagers',
  POWER_USER_PLUS = 'powerUserPlus',
  EOS_STUDY_ADMINS = 'eosStudyAdmins'
}

// Group definitions mapping
export const GROUP_DEFINITIONS: Record<UserGroup, UserRole[]> = {
  [UserGroup.RCM_ADMINS]: [UserRole.ADMIN, UserRole.CONTENT_ADMIN],
  [UserGroup.RCM_USERS]: [UserRole.POWER_USER, UserRole.USER],
  [UserGroup.CONTENT_ADMINS]: [UserRole.ADMIN, UserRole.CONTENT_ADMIN, UserRole.ETMF_MANAGER, UserRole.ETMF_SPECIALIST],
  [UserGroup.REPORTS_ACCESS_ADMINS]: [UserRole.ADMIN, UserRole.CONTENT_ADMIN, UserRole.ETMF_MANAGER],
  [UserGroup.STAGING_ACCESS]: [
    UserRole.ETMF_SITE_USER,
    UserRole.ETMF_STUDY_USER,
    UserRole.ETMF_MANAGER,
    UserRole.ETMF_SPECIALIST,
    UserRole.RCR_USER,
    UserRole.ETMF_SPONSOR
  ],
  [UserGroup.STAGING_USERS]: [UserRole.ETMF_SITE_USER, UserRole.ETMF_STUDY_USER],
  [UserGroup.STAGING_MANAGERS]: [UserRole.ETMF_MANAGER, UserRole.ETMF_SPECIALIST],
  [UserGroup.POWER_USER_PLUS]: [UserRole.POWER_USER],
  [UserGroup.EOS_STUDY_ADMINS]: [UserRole.STUDY_ADMIN]
}

// Access rules mapping
export const ACCESS_RULES: Record<AccessRule, (UserRole | UserGroup)[]> = {
  // System sections
  [AccessRule.SYSTEM_ACCESS]: [UserRole.ADMIN],
  [AccessRule.SYSTEM_ACCESS_USERS]: [UserRole.ADMIN],
  [AccessRule.SYSTEM_ACCESS_GROUPS]: [UserRole.ADMIN],
  [AccessRule.SYSTEM_ACCESS_POLICIES]: [UserRole.ADMIN],
  [AccessRule.SYSTEM_ACCESS_TEXT_AND_MESSAGES]: [UserRole.ADMIN],
  [AccessRule.SYSTEM_ACCESS_SYSTEM_PERFORMANCE]: [UserRole.ADMIN],
  [AccessRule.SYSTEM_ACCESS_ETMF_ACCESS]: [UserRole.ADMIN],
  [AccessRule.SYSTEM_ACCESS_INTEGRATIONS]: [UserRole.ADMIN],
  [AccessRule.SYSTEM_ACCESS_OPERATING_MODE]: [UserRole.ADMIN],
  [AccessRule.SYSTEM_ACCESS_UPGRADE_VALIDATION]: [UserRole.ADMIN],
  [AccessRule.FSI_SETTINGS]: [UserRole.ADMIN],
  [AccessRule.FSI_PROPERTIES]: [UserRole.ADMIN],

  // Settings
  [AccessRule.SETTINGS_ACCESS]: [UserGroup.RCM_ADMINS, UserRole.ETMF_MANAGER],
  [AccessRule.SETTINGS_ACCESS_EDIT_PROFILE]: [UserGroup.RCM_ADMINS, UserGroup.STAGING_ACCESS],
  [AccessRule.CLINICAL_ACCESS]: [UserGroup.CONTENT_ADMINS, UserRole.ETMF_SPONSOR],

  // Reports sections
  [AccessRule.REPORTS_ACCESS]: [
    UserRole.ADMIN,
    UserRole.CONTENT_ADMIN,
    UserRole.ETMF_MANAGER,
    UserGroup.RCM_USERS,
    UserRole.AUDITOR,
    UserRole.ETMF_SPECIALIST,
    UserRole.ETMF_STUDY_USER,
    UserRole.ETMF_SITE_USER,
    UserRole.ETMF_SPONSOR,
    UserGroup.EOS_STUDY_ADMINS
  ],
  [AccessRule.REPORTS_ACCESS_ACTIVITY]: [UserGroup.REPORTS_ACCESS_ADMINS],
  [AccessRule.REPORTS_ACCESS_CLINICAL_ACTIVITY]: [UserGroup.CONTENT_ADMINS, UserRole.ETMF_SPONSOR],
  [AccessRule.REPORTS_ACCESS_ACCESS_RIGHTS]: [UserGroup.REPORTS_ACCESS_ADMINS, UserRole.AUDITOR],
  [AccessRule.REPORTS_ACCESS_READ_ACKNOWLEDGE]: [UserGroup.REPORTS_ACCESS_ADMINS, UserRole.AUDITOR],
  [AccessRule.REPORTS_ACCESS_USERS_USAGE]: [UserGroup.REPORTS_ACCESS_ADMINS, UserRole.AUDITOR],
  [AccessRule.REPORTS_ACCESS_GROUP_MEMBERSHIP]: [UserGroup.REPORTS_ACCESS_ADMINS, UserRole.AUDITOR],
  [AccessRule.REPORTS_ACCESS_CHANGE_REQUESTS]: [UserGroup.REPORTS_ACCESS_ADMINS, UserGroup.RCM_USERS],
  [AccessRule.REPORTS_ACCESS_CHANGE_REQUEST_TYPES]: [UserGroup.REPORTS_ACCESS_ADMINS],
  [AccessRule.REPORTS_ACCESS_PROPERTIES_SEARCH]: [
    UserGroup.CONTENT_ADMINS,
    UserRole.AUDITOR,
    UserRole.POWER_USER,
    UserRole.ETMF_STUDY_USER,
    UserRole.ETMF_SITE_USER,
    UserRole.USER,
    UserRole.ETMF_SPONSOR
  ],
  [AccessRule.REPORTS_ACCESS_PROPERTIES_USAGE]: [UserGroup.REPORTS_ACCESS_ADMINS, UserRole.AUDITOR],
  [AccessRule.REPORTS_ACCESS_TASKS]: [UserGroup.REPORTS_ACCESS_ADMINS, UserRole.AUDITOR, UserRole.USER],
  [AccessRule.REPORTS_ACCESS_REJECTION_REASONS]: [UserGroup.REPORTS_ACCESS_ADMINS, UserRole.AUDITOR],
  [AccessRule.REPORTS_ACCESS_DOCUMENT_EXPIRATION]: [UserGroup.REPORTS_ACCESS_ADMINS, UserRole.AUDITOR],
  [AccessRule.REPORTS_ACCESS_CUSTOM]: [
    UserGroup.CONTENT_ADMINS,
    UserGroup.RCM_USERS,
    UserRole.AUDITOR,
    UserRole.ETMF_SPONSOR
  ],
  [AccessRule.REPORTS_ACCESS_EOS_DOWNLOAD_ACTIVITY]: [UserGroup.RCM_ADMINS, UserRole.POWER_USER],
  [AccessRule.REPORTS_ACCESS_EOS_ACKNOWLEDGEMENT_ACTIVITY]: [UserGroup.RCM_ADMINS, UserRole.POWER_USER],
  [AccessRule.REPORTS_EOS]: [UserGroup.RCM_ADMINS, UserRole.POWER_USER, UserRole.STUDY_ADMIN],
  [AccessRule.REPORTS_ACCESS_ETMF_COMPLETION]: [
    UserGroup.RCM_ADMINS,
    UserRole.ETMF_MANAGER,
    UserRole.ETMF_SPECIALIST,
    UserRole.ETMF_SPONSOR
  ],
  [AccessRule.REPORTS_ACCESS_ETMF_DOC_DETAIL]: [
    UserGroup.RCM_ADMINS,
    UserRole.ETMF_MANAGER,
    UserRole.ETMF_SPECIALIST,
    UserRole.ETMF_SPONSOR
  ],
  [AccessRule.REPORTS_ACCESS_ETMF_FILED_DOCS]: [
    UserGroup.RCM_ADMINS,
    UserRole.ETMF_MANAGER,
    UserRole.ETMF_SPECIALIST,
    UserRole.ETMF_SPONSOR
  ],
  [AccessRule.REPORTS_ACCESS_ETMF_CONTRIBUTOR_DOC_STATUS]: [
    UserGroup.RCM_ADMINS,
    UserRole.ETMF_MANAGER,
    UserRole.ETMF_SPECIALIST,
    UserRole.ETMF_SPONSOR
  ],
  [AccessRule.REPORTS_ACCESS_DUPLICATE_DOCUMENTS]: [
    UserGroup.RCM_ADMINS,
    UserRole.POWER_USER,
    UserRole.ETMF_MANAGER,
    UserRole.ETMF_SPECIALIST
  ],
  [AccessRule.REPORTS_ACCESS_QC_REVIEW]: [UserGroup.CONTENT_ADMINS, UserRole.POWER_USER],
  [AccessRule.REPORTS_ACCESS_ETMF_TIMELINESS]: [
    UserGroup.RCM_ADMINS,
    UserRole.ETMF_MANAGER,
    UserRole.ETMF_SPECIALIST,
    UserRole.ETMF_SPONSOR
  ],

  // EOS Media Access
  [AccessRule.EOS_MEDIA_ACCESS]: [
    UserGroup.RCM_ADMINS,
    UserGroup.RCM_USERS,
    UserRole.AUDITOR,
    UserGroup.EOS_STUDY_ADMINS
  ],
  [AccessRule.EOS_MEDIA_ACCESS_OVERVIEW]: [UserGroup.RCM_ADMINS, UserRole.POWER_USER, UserGroup.EOS_STUDY_ADMINS],
  [AccessRule.EOS_MEDIA_ACCESS_TRANSFER_MEDIA]: [UserGroup.RCM_ADMINS, UserGroup.EOS_STUDY_ADMINS],
  [AccessRule.EOS_MEDIA_ACCESS_SET_UP]: [UserGroup.RCM_ADMINS, UserGroup.EOS_STUDY_ADMINS],
  [AccessRule.EOS_MEDIA_ACCESS_DOWNLOAD_MEDIA]: [
    UserGroup.RCM_ADMINS,
    UserGroup.RCM_USERS,
    UserRole.AUDITOR,
    UserGroup.EOS_STUDY_ADMINS
  ],
  [AccessRule.EOS_MEDIA_ACCESS_SET_UP_POWER_USER]: [UserGroup.RCM_ADMINS, UserGroup.POWER_USER_PLUS],

  // Filter and Notifications
  [AccessRule.FILTER_ROW_HIGH_RESTRICTION]: [UserGroup.CONTENT_ADMINS, UserRole.AUDITOR],
  [AccessRule.NOTIFICATIONS_ACTIONS]: [UserGroup.CONTENT_ADMINS, UserRole.POWER_USER, UserRole.RCR_USER],

  // Tasks
  [AccessRule.TASKS_ACCESS]: [
    UserGroup.RCM_ADMINS,
    UserGroup.RCM_USERS,
    UserRole.EXTERNAL,
    UserGroup.STAGING_ACCESS,
    UserRole.ETMF_SPONSOR,
    UserGroup.EOS_STUDY_ADMINS
  ],
  [AccessRule.TASKS_ACCESS_ALL_OPEN]: [UserGroup.CONTENT_ADMINS, UserRole.ETMF_SPONSOR, UserGroup.EOS_STUDY_ADMINS],
  [AccessRule.TASKS_FILTERS]: [
    UserGroup.CONTENT_ADMINS,
    UserRole.POWER_USER,
    UserRole.ETMF_SPONSOR,
    UserGroup.EOS_STUDY_ADMINS
  ],

  // Workflows
  [AccessRule.WORKFLOWS_FILTERS]: [
    UserGroup.CONTENT_ADMINS,
    UserRole.POWER_USER,
    UserRole.RCR_USER,
    UserRole.ETMF_SPONSOR,
    UserGroup.EOS_STUDY_ADMINS
  ],
  [AccessRule.WORKFLOWS_ACCESS]: [
    UserGroup.CONTENT_ADMINS,
    UserGroup.RCM_USERS,
    UserRole.RCR_USER,
    UserRole.ETMF_SPONSOR,
    UserGroup.EOS_STUDY_ADMINS
  ],
  [AccessRule.WORKFLOWS_ACCESS_DETAIL]: [
    UserGroup.CONTENT_ADMINS,
    UserGroup.RCM_USERS,
    UserRole.AUDITOR,
    UserRole.ETMF_SPONSOR,
    UserGroup.EOS_STUDY_ADMINS
  ],

  // Documents
  [AccessRule.DOCUMENTS_ACCESS]: [
    UserGroup.CONTENT_ADMINS,
    UserGroup.RCM_USERS,
    UserRole.AUDITOR,
    UserRole.EXTERNAL,
    UserRole.INSPECTOR,
    UserRole.RCR_USER,
    UserGroup.STAGING_USERS,
    UserRole.ETMF_SPONSOR,
    UserGroup.EOS_STUDY_ADMINS
  ],
  [AccessRule.DOCUMENTS_SELECT_MULTI_FILES]: [
    UserGroup.CONTENT_ADMINS,
    UserRole.POWER_USER,
    UserRole.USER,
    UserRole.EXTERNAL,
    UserRole.ETMF_SITE_USER,
    UserRole.ETMF_STUDY_USER,
    UserGroup.EOS_STUDY_ADMINS
  ],
  [AccessRule.DOCUMENTS_ACCESS_PROPERTIES_SEARCH]: [
    UserGroup.CONTENT_ADMINS,
    UserRole.POWER_USER,
    UserRole.AUDITOR,
    UserRole.RCR_USER,
    UserRole.USER,
    UserRole.ETMF_SPONSOR,
    UserGroup.EOS_STUDY_ADMINS
  ],
  [AccessRule.DOCUMENTS_ACCESS_RECENT]: [
    UserGroup.RCM_ADMINS,
    UserGroup.RCM_USERS,
    UserRole.EXTERNAL,
    UserGroup.STAGING_ACCESS,
    UserRole.RCR_USER,
    UserGroup.EOS_STUDY_ADMINS
  ],
  [AccessRule.DOCUMENTS_ACCESS_PERIODIC_REVIEW]: [UserGroup.RCM_ADMINS, UserGroup.RCM_USERS],
  [AccessRule.DOCUMENTS_ACCESS_REGULATED_CONTENT]: [
    UserGroup.RCM_ADMINS,
    UserGroup.RCM_USERS,
    UserRole.EXTERNAL,
    UserRole.INSPECTOR,
    UserRole.AUDITOR,
    UserGroup.STAGING_ACCESS,
    UserGroup.EOS_STUDY_ADMINS
  ],
  [AccessRule.DOCUMENTS_ACCESS_FORMS]: [UserGroup.CONTENT_ADMINS, UserRole.RCR_USER],
  [AccessRule.DOCUMENTS_ACCESS_TEMPLATES]: [UserGroup.CONTENT_ADMINS, UserRole.POWER_USER, UserRole.RCR_USER],
  [AccessRule.DOCUMENTS_ACCESS_QC_REVIEW]: [UserGroup.CONTENT_ADMINS, UserRole.POWER_USER],
  [AccessRule.DOCUMENTS_ACCESS_CHANGE_REQUESTS]: [UserGroup.RCM_ADMINS, UserGroup.RCM_USERS],
  [AccessRule.DOCUMENTS_FILTER_OBSOLETE]: [UserGroup.CONTENT_ADMINS, UserRole.POWER_USER, UserRole.RCR_USER],
  [AccessRule.SEARCH_FILTER_OBSOLETE]: [UserGroup.CONTENT_ADMINS, UserRole.POWER_USER, UserRole.RCR_USER],
  [AccessRule.SHOW_FILE_ACCESS]: [
    UserGroup.RCM_ADMINS,
    UserGroup.RCM_USERS,
    UserRole.EXTERNAL,
    UserRole.AUDITOR,
    UserRole.INSPECTOR,
    UserGroup.STAGING_ACCESS,
    UserRole.RCR_USER,
    UserGroup.EOS_STUDY_ADMINS
  ],

  // Homepage
  [AccessRule.HOMEPAGE_ACCESS_REPORTS]: [
    UserGroup.RCM_ADMINS,
    UserGroup.RCM_USERS,
    UserRole.AUDITOR,
    UserRole.RCR_USER,
    UserGroup.EOS_STUDY_ADMINS
  ],
  [AccessRule.HOMEPAGE_ACCESS_RECENT]: [
    UserGroup.RCM_ADMINS,
    UserGroup.RCM_USERS,
    UserRole.EXTERNAL,
    UserRole.RCR_USER,
    UserGroup.EOS_STUDY_ADMINS
  ],
  [AccessRule.HOMEPAGE_ACCESS_PERIODIC_REVIEW]: [UserGroup.RCM_ADMINS, UserGroup.RCM_USERS],
  [AccessRule.HOMEPAGE_ACCESS_TASKS]: [
    UserGroup.RCM_ADMINS,
    UserGroup.RCM_USERS,
    UserRole.EXTERNAL,
    UserRole.RCR_USER,
    UserGroup.EOS_STUDY_ADMINS
  ],
  [AccessRule.HOMEPAGE_ACCESS_FAVORITES]: [
    UserGroup.RCM_ADMINS,
    UserGroup.RCM_USERS,
    UserRole.EXTERNAL,
    UserRole.AUDITOR,
    UserRole.INSPECTOR,
    UserRole.RCR_USER,
    UserGroup.EOS_STUDY_ADMINS
  ],
  [AccessRule.HOMEPAGE_ACCESS_WORKFLOWS]: [
    UserGroup.RCM_ADMINS,
    UserGroup.RCM_USERS,
    UserRole.RCR_USER,
    UserGroup.EOS_STUDY_ADMINS
  ],

  // Group access rules (allows direct group reference like legacy AngularJS)
  // These map group names to their group definitions, enabling hasAccess('stagingUsers') style calls
  // that reference groups directly (matching the _.extend pattern in legacy accessService.rightsTable.js)
  [AccessRule.RCM_ADMINS]: [UserGroup.RCM_ADMINS],
  [AccessRule.RCM_USERS]: [UserGroup.RCM_USERS],
  [AccessRule.CONTENT_ADMINS]: [UserGroup.CONTENT_ADMINS],
  [AccessRule.REPORTS_ACCESS_ADMINS]: [UserGroup.REPORTS_ACCESS_ADMINS],
  [AccessRule.STAGING_ACCESS]: [UserGroup.STAGING_ACCESS],
  [AccessRule.STAGING_USERS]: [UserGroup.STAGING_USERS],
  [AccessRule.STAGING_MANAGERS]: [UserGroup.STAGING_MANAGERS],
  [AccessRule.POWER_USER_PLUS]: [UserGroup.POWER_USER_PLUS],
  [AccessRule.EOS_STUDY_ADMINS]: [UserGroup.EOS_STUDY_ADMINS]
}

// Access check result interface
export interface AccessCheckResult {
  isAllowed: boolean
  reason?: string
}

// Async access options
export interface AsyncAccessOptions {
  hideDontAccessErrorMsg?: boolean
  redirectOnFailure?: boolean
  customErrorMessage?: string
}

// QC access result
export interface QCAccessResult {
  isEnabled: boolean
  hasAccess: boolean
  reason?: string
}

// Permission checking types
export type AccessReference = AccessRule | UserRole[] | string

// Legacy action object for backward compatibility
export interface LegacyAccessAction {
  availableRoles?: string
  [key: string]: unknown
}

// Features info interface
export interface FeaturesInfo {
  qcReview?: {
    qcReviewEnabled: boolean
  }
}

// App status interface
export interface AppStatus {
  qcReviewEnabled: boolean
}

// Access service interface
export interface IAccessService {
  isAllowed(ref: AccessReference): boolean
  isNotAllowed(ref: AccessReference): boolean
  isAllowedStateAsync(ref: AccessReference, options?: AsyncAccessOptions): Promise<boolean>
  isNotAllowedAsync(ref: AccessReference, options?: AsyncAccessOptions): Promise<boolean>
  isQCAccessAllowedAsync(): Promise<void>
  passesCondition(condition: boolean): Promise<void>
  areRolesPermitted(allowedRoles: UserRole[]): boolean
  checkUserAccess(userRoles: UserRole[], requiredRoles: (UserRole | UserGroup)[]): boolean
}

// Hook result interfaces
export interface UseAccessControlResult {
  isAllowed: (ref: AccessReference) => boolean
  isNotAllowed: (ref: AccessReference) => boolean
  checkAccess: (ref: AccessReference) => AccessCheckResult
  hasRole: (role: UserRole) => boolean
  hasAnyRole: (roles: UserRole[]) => boolean
  isInGroup: (group: UserGroup) => boolean
}

export interface UseAsyncAccessResult {
  checkAccessAsync: (ref: AccessReference, options?: AsyncAccessOptions) => Promise<boolean>
  isQCAccessAllowed: () => Promise<boolean>
  checkCondition: (condition: boolean) => Promise<boolean>
  isLoading: boolean
  error: Error | null
}

export interface UseAccessRulesResult {
  getAccessRule: (rule: AccessRule) => (UserRole | UserGroup)[]
  getAllAccessRules: () => Record<AccessRule, (UserRole | UserGroup)[]>
  getGroupDefinition: (group: UserGroup) => UserRole[]
  expandGroups: (entities: (UserRole | UserGroup)[]) => UserRole[]
}
